import { useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import Raven from 'raven-js';
import ACSkeletonBox from 'academy-common-lib/components/loading/ACSkeletonBox';
import { useSyncStaticStateInsideIFrame } from 'embedded-academy-lib/insideFrame';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { Link as TLink } from 'foundations-components/transitional/components/Link';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Error } from '../../error/components/Error';
import { ErrorBoundary } from '../../error';
import { Metrics } from '../../../Metrics';
import { SkeletonGroup } from '../../dynamicContentWidget/components/SkeletonGroup';
import { useStaticContentState } from '../staticContentWidgetHooks';
import { WidgetWrapper } from '../../layout';
import { Video, VideoSection } from '../../video';
import { getAcademyRedirectUrl, getStaticLessonRedirectUrl, getStaticShortsRedirectUrl } from '../urlUtils';
import { usageTracker } from '../../../utils/usageTracker';
// @ts-expect-error untyped module
import { useParentAppName } from '../../../hooks/searchParamHooks';
import { useLesson, useShorts } from '../../../hooks/contentHooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StaticContentLoading = ({
  variant = 'default'
}) => /*#__PURE__*/_jsx(NavMarker, {
  name: "STATIC_WIDGET_LOADING",
  children: /*#__PURE__*/_jsxs(WidgetWrapper, {
    children: [/*#__PURE__*/_jsx(ACSkeletonBox, {
      animated: true,
      className: "m-bottom-2",
      style: {
        width: '100%',
        aspectRatio: '16/9'
      }
    }), variant === 'default' && /*#__PURE__*/_jsx(SkeletonGroup, {
      className: "m-top-8",
      rows: 5,
      variableWidth: true
    })]
  })
});
const StaticContentNotFound = ({
  status
}) => {
  const {
    onNoContent
  } = useSyncStaticStateInsideIFrame();
  const {
    lessonId,
    contentId,
    shortId,
    variant,
    authType
  } = useStaticContentState();
  const parentAppName = useParentAppName();
  const currentVariant = variant || 'default';
  const widgetType = authType || 'app';
  useEffect(() => {
    usageTracker.track('pageView', Object.assign({
      screen: `static-content-widget-${widgetType}-${currentVariant}-not-found`,
      contentType: lessonId ? 'LESSON' : 'SHORT_FORM_CONTENT'
    }, contentId && {
      contentId
    }, lessonId && {
      lessonId
    }, shortId && {
      shortId
    }));
    Metrics.counter('static-content-widget-not-found', {
      variant: currentVariant,
      authType: widgetType,
      hostAppName: parentAppName
    }).increment();
    onNoContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId, lessonId, onNoContent, shortId, variant]);
  if (status === 403) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "STATIC_RESULTS_FORBIDDEN",
      children: /*#__PURE__*/_jsx(WidgetWrapper, {
        children: /*#__PURE__*/_jsx(ErrorMessage, {
          illustration: "lock",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyCommon.errors.forbidden.heading"
          }),
          type: "403",
          children: /*#__PURE__*/_jsx(TLink, {
            "data-test-id": "embedded-academy-not-found-redirect-link",
            external: true,
            href: getAcademyRedirectUrl(),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "staticWidget.notFound.link"
            })
          })
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "STATIC_RESULTS_NOT_FOUND",
    children: /*#__PURE__*/_jsx(WidgetWrapper, {
      children: /*#__PURE__*/_jsx(ErrorMessage, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "staticWidget.notFound.title"
        }),
        type: "notFound",
        children: /*#__PURE__*/_jsx(TLink, {
          "data-test-id": "embedded-academy-not-found-redirect-link",
          external: true,
          href: getAcademyRedirectUrl(),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "staticWidget.notFound.link"
          })
        })
      })
    })
  });
};
const StaticContentError = ({
  error
}) => {
  const {
    onError
  } = useSyncStaticStateInsideIFrame();
  const {
    authType
  } = useStaticContentState();
  const parentAppName = useParentAppName();
  const errorStatus = (error === null || error === void 0 ? void 0 : error.status) || 500;
  const widgetType = authType || 'app';
  useEffect(() => {
    if (![404, 403].includes(errorStatus)) {
      Metrics.counter('static-content-error', {
        hostAppName: parentAppName,
        authType: widgetType
      }).increment();
      Raven.captureException(error);
      onError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onError, error, errorStatus]);
  const renderError = () => {
    switch (errorStatus) {
      case 404:
      case 403:
        return /*#__PURE__*/_jsx(StaticContentNotFound, {
          status: errorStatus
        });
      default:
        return /*#__PURE__*/_jsx(Error, {});
    }
  };
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    marker: "STATIC_RESULTS_PAGE_ERROR",
    children: /*#__PURE__*/_jsx(WidgetWrapper, {
      children: renderError()
    })
  });
};
export const StaticContentResult = ({
  isResizeReady = true
}) => {
  var _lesson$content;
  const {
    authType = 'app',
    lessonId,
    contentId,
    shortId,
    language,
    variant,
    startTime,
    Link,
    Title,
    Description,
    renderContext,
    enablePictureInPicture,
    hublet
  } = useStaticContentState();
  const {
    onContent,
    onVideoFinish,
    onVideoPlay,
    onVideoEngagement
  } = useSyncStaticStateInsideIFrame();
  const isLessonContent = Boolean(lessonId && contentId);
  const isPublic = authType === 'public';
  const {
    lesson,
    lessonLoading,
    lessonError
  } = useLesson(lessonId, language, isPublic);
  const {
    shortFormContent,
    shortFormContentError,
    shortFormContentLoading
  } = useShorts(shortId, isPublic);

  // @ts-expect-error each content type has an id field
  const lessonContent = lesson === null || lesson === void 0 || (_lesson$content = lesson.content) === null || _lesson$content === void 0 ? void 0 : _lesson$content.find(c => c.id === Number(contentId));
  const content = lessonContent || shortFormContent;
  const videoStartTime = startTime ? Number(startTime) : undefined;
  const creatorHubId = content && content.hubId;
  const videoType = content && content.type;
  const redirectLink = isLessonContent && lesson ? getStaticLessonRedirectUrl(lessonId, contentId, language, videoStartTime) : getStaticShortsRedirectUrl(shortId, videoStartTime);
  useEffect(() => {
    if (isLessonContent && content) {
      onContent({
        contentName: content.name,
        contentType: 'LESSON',
        contentId: contentId,
        lessonId: lessonId,
        language: content.language,
        sourceUrl: redirectLink
      });
    }
    if (!isLessonContent && content) {
      onContent({
        contentName: content.name,
        contentDescription: content.description,
        shortId: shortId,
        contentType: 'SHORT_FORM_CONTENT',
        language: content.language,
        sourceUrl: redirectLink
      });
    }
    if (content) {
      const currentVariant = variant || 'default';
      const widgetType = (authType || 'app').toLowerCase();
      usageTracker.track('pageView', Object.assign({
        screen: `static-content-widget-${widgetType}-${currentVariant}`,
        contentType: isLessonContent ? 'LESSON' : 'SHORT_FORM_CONTENT'
      }, contentId && {
        contentId
      }, lessonId && {
        lessonId
      }, shortId && {
        shortId
      }));
      Metrics.counter('static-content-widget-success', {
        variant: currentVariant,
        authType: widgetType
      }).increment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onContent, isLessonContent, content, contentId, lessonId, shortId]);

  // To handle the case where error page is shown until lesson or short id is available from state
  if (!lessonId && !shortId) return null;

  // Handle the not found state if the content is missing and there is a lesson. For shorts, this case is handled in StaticContentError
  if (!lessonContent && lesson) {
    return /*#__PURE__*/_jsx(StaticContentNotFound, {});
  }
  if (lessonLoading || shortFormContentLoading) {
    return /*#__PURE__*/_jsx(StaticContentLoading, {
      variant: isResizeReady ? variant : 'video'
    });
  }
  if (lessonError || shortFormContentError || !content) {
    return /*#__PURE__*/_jsx(StaticContentError, {
      error: lessonError || shortFormContentError
    });
  }
  if (variant === 'video') {
    return /*#__PURE__*/_jsx(ErrorBoundary, {
      marker: "STATIC_RESULTS_PAGE_ERROR",
      children: /*#__PURE__*/_jsx(NavMarker, {
        name: "STATIC_VIDEO_LOADED",
        children: /*#__PURE__*/_jsx(WidgetWrapper, {
          children: /*#__PURE__*/_jsx(Video, {
            contentId: Number(contentId),
            creatorHubId: creatorHubId,
            enablePictureInPicture: enablePictureInPicture,
            hublet: hublet,
            isPublic: isPublic,
            language: language || (content === null || content === void 0 ? void 0 : content.language),
            lessonId: Number(lessonId),
            link: redirectLink,
            onEngagement: onVideoEngagement,
            onFinish: onVideoFinish,
            onPlay: onVideoPlay,
            renderContext: renderContext || 'embedded-academy',
            shortId: shortId,
            startTime: videoStartTime,
            videoType: videoType
          })
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    marker: "STATIC_RESULTS_PAGE_ERROR",
    children: /*#__PURE__*/_jsx(NavMarker, {
      name: "STATIC_RESULTS_LOADED",
      children: /*#__PURE__*/_jsx(WidgetWrapper, {
        children: /*#__PURE__*/_jsx(VideoSection, Object.assign({
          ContentDescriptionComponent: Description,
          ContentNameComponent: Title,
          LinkComponent: Link,
          contentId: Number(contentId),
          contentName: content.name,
          creatorHubId: creatorHubId,
          hublet: hublet,
          isPublic: isPublic,
          isResizeReady: isResizeReady,
          language: language || content.language,
          lessonId: Number(lessonId),
          link: redirectLink,
          onEngagement: onVideoEngagement,
          onFinish: onVideoFinish,
          onLinkClick: () => {
            usageTracker.track('interaction', Object.assign({
              action: 'static-widget-link-click',
              contentType: isLessonContent ? 'LESSON' : 'SHORT_FORM_CONTENT'
            }, contentId && {
              contentId
            }, lessonId && {
              lessonId
            }, shortId && {
              shortId
            }));
            Metrics.counter('static-widget-link-to-academy-clicked', {
              variant: variant || 'default'
            }).increment();
          },
          onPlay: onVideoPlay,
          renderContext: renderContext || 'embedded-academy',
          shortId: shortId
        }, !isLessonContent && {
          contentDescription: content.description
        }, {
          enablePictureInPicture: enablePictureInPicture,
          videoTimestamp: videoStartTime,
          videoType: videoType
        }))
      })
    })
  });
};